import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || "https://demo.queue.falits.com/api";

const api = axios.create({ baseURL: BASE_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("eq_access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const refresh = localStorage.getItem("eq_refresh_token");
        const { data } = await axios.post(`${BASE_URL}/auth/token/refresh/`, { refresh });
        localStorage.setItem("eq_access_token", data.access);
        original.headers.Authorization = `Bearer ${data.access}`;
        return api(original);
      } catch {
        localStorage.removeItem("eq_access_token");
        localStorage.removeItem("eq_refresh_token");
        localStorage.removeItem("eq_user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

const publicApi = axios.create({ baseURL: BASE_URL });

// ── Auth ──────────────────────────────────────────────────────────────────────
export const auth = {
  login:               (data)  => publicApi.post("/auth/token/", data),
  refresh:             (r)     => publicApi.post("/auth/token/refresh/", { refresh: r }),
  me:                  ()      => api.get("/auth/me/"),
  forceChangePassword: (data)  => api.post("/auth/force-change-password/", data),
  changePassword:      (data)  => api.post("/auth/staff/change-password/", data),
};

// ── Super Admin ───────────────────────────────────────────────────────────────
export const superAdmin = {
  // Organisations
  listOrgs:         (params)   => api.get("/organisations/", { params }),
  createOrg:        (data)     => api.post("/organisations/", data),
  getOrg:           (id)       => api.get(`/organisations/${id}/`),
  updateOrg:        (id, data) => api.patch(`/organisations/${id}/`, data),
  suspendOrg:       (id)       => api.post(`/organisations/${id}/suspend/`),
  activateOrg:      (id)       => api.post(`/organisations/${id}/activate/`),
  getOrgBranches:   (id)       => api.get(`/organisations/${id}/branches/`),
  addBranch:        (id, data) => api.post(`/organisations/${id}/add-branch/`, data),
  attachFacility:   (id, data) => api.post(`/organisations/${id}/attach-facility/`, data),
  getOrgAdmins:     (id)       => api.get(`/organisations/${id}/admins/`),
  addOrgAdmin:      (id, data) => api.post(`/organisations/${id}/add-admin/`, data),

  // Facilities (standalone)
  listFacilities:   (params)   => api.get("/facilities/", { params }),
  createFacility:   (data)     => api.post("/facilities/", data),
  updateFacility:   (id, data) => api.patch(`/facilities/${id}/`, data),
  suspendFacility:  (id)       => api.post(`/facilities/${id}/suspend/`),
  activateFacility: (id)       => api.post(`/facilities/${id}/activate/`),
  resendInvite:     (id)       => api.post(`/facilities/${id}/resend-invite/`),
  platformStats:    ()         => api.get("/facilities/platform-stats/"),

  // Vendor Branding (super admin only)
  getVendorBranding:    ()         => api.get("/facility/vendor-branding/"),
  updateVendorBranding: (data)     => api.patch("/facility/vendor-branding/", data),
  toggleFacilityBranding: (id)     => api.post(`/facilities/${id}/toggle-branding/`),

  // Org admin management
  getOrgEligibleAdmins: (id)       => api.get(`/organisations/${id}/eligible-admins/`),
  assignOrgAdmin:       (id, data) => api.post(`/organisations/${id}/assign-admin/`, data),

  // Upgrade facility to org
  upgradeToOrg: (id, data) => api.post(`/facilities/${id}/upgrade-to-org/`, data),

  // Users
  listUsers:      (params)   => api.get("/auth/staff/", { params }),
  resetPassword:  (id, data) => api.post(`/auth/staff/${id}/reset-password/`, data),
  deactivateUser: (id)       => api.post(`/auth/staff/${id}/deactivate/`),
  reactivateUser: (id)       => api.post(`/auth/staff/${id}/reactivate/`),
  unlockUser:     (id)       => api.post(`/auth/staff/${id}/unlock/`),
};

// ── Org Admin ─────────────────────────────────────────────────────────────────
export const orgAdmin = {
  // My organisation
  getMyOrg:         ()         => api.get("/organisations/").then(r => r.data?.results?.[0] || r.data?.[0]),
  getOrg:           (id)       => api.get(`/organisations/${id}/`),
  getBranches:      (id)       => api.get(`/organisations/${id}/branches/`),
  addBranch:        (id, data) => api.post(`/organisations/${id}/add-branch/`, data),
  getAdmins:        (id)       => api.get(`/organisations/${id}/admins/`),
  addAdmin:         (id, data) => api.post(`/organisations/${id}/add-admin/`, data),

  // Branch management (org admin enters a branch)
  getBranch:        (id)       => api.get(`/facilities/${id}/`),
  updateBranch:     (id, data) => api.patch(`/facilities/${id}/`, data),
  suspendBranch:    (id)       => api.post(`/facilities/${id}/suspend/`),
  activateBranch:   (id)       => api.post(`/facilities/${id}/activate/`),
  getBranchSettings:(id)       => api.get(`/facility/settings/?facility=${id}`),
  getBranchStaff:   (params)   => api.get("/auth/staff/", { params }),
  createBranchStaff:(data)     => api.post("/auth/staff/", data),
  resetBranchUserPw:(id, data) => api.post(`/auth/staff/${id}/reset-password/`, data),
  getBranchZones:   (params)   => api.get("/zones/", { params }),
  getBranchStations:(params)   => api.get("/stations/", { params }),
  updateBranchSettings:(data)  => api.patch("/facility/settings/", data),

  // Org-level staff (all branches)
  listOrgStaff:     (params)   => api.get("/auth/staff/", { params }),
  transferStaff:    (id, data) => api.post(`/auth/staff/${id}/transfer/`, data),

  // Cross-branch reports
  reportOverview:   (id)       => api.get(`/organisations/${id}/reports/overview/`),
  reportBranches:   (id, p)    => api.get(`/organisations/${id}/reports/branches/`,   { params: p }),
  reportBottlenecks:(id, p)    => api.get(`/organisations/${id}/reports/bottlenecks/`,{ params: p }),
  reportStaff:      (id, p)    => api.get(`/organisations/${id}/reports/staff/`,      { params: p }),
  reportHourly:     (id, p)    => api.get(`/organisations/${id}/reports/hourly/`,     { params: p }),

  // Activity log
  getActivityLog:   (params)   => api.get("/activity-log/", { params }),
  markLogsRead:     ()         => api.post("/activity-log/mark-read/"),
  unreadCount:      ()         => api.get("/activity-log/unread-count/"),
};

// ── Facility Admin ────────────────────────────────────────────────────────────
export const facilityAdmin = {
  getSettings:    ()       => api.get("/facility/settings/"),
  updateSettings: (data)   => api.patch("/facility/settings/", data),
  listKioskFields:    ()         => api.get("/facility/kiosk-fields/"),
  updateKioskField:   (id, data) => api.patch(`/facility/kiosk-fields/${id}/`, data),
  reorderKioskFields: (items)    => api.post("/facility/kiosk-fields/reorder/", { items }),
  listInsuranceRules:  ()         => api.get("/facility/insurance-rules/"),
  createInsuranceRule: (data)     => api.post("/facility/insurance-rules/", data),
  updateInsuranceRule: (id, data) => api.patch(`/facility/insurance-rules/${id}/`, data),
  deleteInsuranceRule: (id)       => api.delete(`/facility/insurance-rules/${id}/`),
  listZones:   ()          => api.get("/zones/"),
  createZone:  (data)      => api.post("/zones/", data),
  updateZone:  (id, data)  => api.patch(`/zones/${id}/`, data),
  deleteZone:  (id)        => api.delete(`/zones/${id}/`),
  listStations:  (params)  => api.get("/stations/", { params }),
  createStation: (data)    => api.post("/stations/", data),
  updateStation: (id, d)   => api.patch(`/stations/${id}/`, d),
  deleteStation: (id)      => api.delete(`/stations/${id}/`),
  listCounters:  (params)  => api.get("/counters/", { params }),
  createCounter: (data)    => api.post("/counters/", data),
  updateCounter: (id, data)=> api.patch(`/counters/${id}/`, data),
  deleteCounter: (id)      => api.delete(`/counters/${id}/`),
  listOutcomes:  (params)  => api.get("/outcomes/", { params }),
  createOutcome: (data)    => api.post("/outcomes/", data),
  updateOutcome: (id, data)=> api.patch(`/outcomes/${id}/`, data),
  deleteOutcome: (id)      => api.delete(`/outcomes/${id}/`),
  listStaff:          ()         => api.get("/auth/staff/"),
  createStaff:        (data)     => api.post("/auth/staff/", data),
  updateStaff:        (id, data) => api.patch(`/auth/staff/${id}/`, data),
  resetStaffPassword: (id, data) => api.post(`/auth/staff/${id}/reset-password/`, data),
  deactivateStaff:    (id)       => api.post(`/auth/staff/${id}/deactivate/`),
  reactivateStaff:    (id)       => api.post(`/auth/staff/${id}/reactivate/`),
  listAudio:   ()     => api.get("/audio/"),
  uploadAudio: (data) => api.post("/audio/", data, { headers: { "Content-Type": "multipart/form-data" } }),
  deleteAudio: (id)   => api.delete(`/audio/${id}/`),
  dailyReport:   (params) => api.get("/reports/daily/",    { params }),
  staffReport:   (params) => api.get("/reports/staff/",    { params }),
  stationReport: (params) => api.get("/reports/stations/", { params }),
  aiRecommendations: (params) => api.get("/ai/recommendations/", { params }),
  activityLog:   (params) => api.get("/activity-log/",    { params }),
  markLogsRead:  ()       => api.post("/activity-log/mark-read/"),
  unreadCount:   ()       => api.get("/activity-log/unread-count/"),
};

// ── Staff ─────────────────────────────────────────────────────────────────────
export const staffQueue = {
  myQueue:       ()               => api.get("/queue/my-queue/"),
  callNext:      (data)           => api.post("/queue/call-next/", data),
  transfer:      (ticketId, data) => api.post(`/queue/${ticketId}/transfer/`, data),
  reAnnounce:    (ticketId)       => api.post(`/queue/${ticketId}/re-announce/`),
  issueManual:   (data)           => api.post("/queue/issue-manual/", data),
  todayStats:    ()               => api.get("/queue/today-stats/"),
  myRanking:     ()               => api.get("/queue/my-ranking/"),
  weeklyStats:   ()               => api.get("/queue/weekly-stats/"),
  monthlyStats:  ()               => api.get("/queue/monthly-stats/"),
  compareStats:  (period)         => api.get("/queue/compare-stats/", { params: { period } }),
  goAway:        ()               => api.post("/queue/go-away/"),
  imBack:        ()               => api.post("/queue/im-back/"),
  selectCounter: (counter_id)     => api.post("/queue/select-counter/", { counter_id }),
};

// ── Public ────────────────────────────────────────────────────────────────────
export const publicQueue = {
  kioskConfig:  (slug)         => publicApi.get(`/queue/${slug}/kiosk-config/`),
  issueTicket:  (slug, data)   => publicApi.post(`/queue/${slug}/issue/`, data),
  displayState: (slug, params) => publicApi.get(`/queue/${slug}/display/`, { params }),
  trackTicket:  (token)        => publicApi.get(`/queue/track/${token}/`),
};
